import {AsyncInlineError, inlineError} from '@s-ui/decorators'

import {IdSharedValueObject} from '../../shared/ValueObjects/IdSharedValueObject.js'
import {UserAdEntity} from '../../userAds/Entities/UserAdEntity.js'
import {GetAdIdByCategoryAdPublisherError} from '../Errors/GetAdIdByCategoryAdPublisherError.js'
import {GetFreeAdsAvailableAdPublisherError} from '../Errors/GetFreeAdsAvailableAdPublisherError.js'
import {FreeAdsAvailableAdPublisherValueObject} from '../ValueObjects/FreeAdsAvailableAdPublisherValueObject.js'
import {DraftRepository} from './DraftRepository.js'

export class HTTPDraftRepository extends DraftRepository {
  /**
   * @constructor
   * @param {Object}    deps
   * @param {Config}    deps.config
   * @param {Fetcher}   deps.fetchOrFail
   * @param {Function}  deps.invalidDraftError
   */
  constructor({
    config,
    fetchOrFail,
    successResponseDraftVOFactory,
    uploadResponseDraftVOFactory,
    draftEntityFactory,
    ongoingDraftResponseFactory,
    invalidDraftError,
    notFoundDraftError,
    resultPublishDraftVOFactory
  }) {
    super({config})

    this._config = config
    this._apiUrl = this._config.get('API_URL')
    this._publisherUsersUrl = `${this._apiUrl}/publisher/users/`
    this._fetchOrFail = fetchOrFail
    this._successResponseDraftVOFactory = successResponseDraftVOFactory
    this._uploadResponseDraftVOFactory = uploadResponseDraftVOFactory
    this._draftEntityFactory = draftEntityFactory
    this._ongoingDraftResponseFactory = ongoingDraftResponseFactory
    this._invalidDraftError = invalidDraftError
    this._notFoundDraftError = notFoundDraftError
    this._resultPublishDraftVOFactory = resultPublishDraftVOFactory
    this._NOT_FOUND_STATUS_CODE = this._config.get('COMMON').HTTP_STATUS_CODES.NOT_FOUND
    this._GENERIC_ERROR = this._config.get('COMMON').ERRORS.GENERIC

    const {DEFAULT_USER_AD_CATEGORY, USER_AD_CATEGORY_RESOURCES} = this._config.get('seller')
    this._DEFAULT_USER_AD_CATEGORY = DEFAULT_USER_AD_CATEGORY
    this._USER_AD_CATEGORY_RESOURCES = USER_AD_CATEGORY_RESOURCES
  }

  _getCategoryResource(categoryId) {
    return this._USER_AD_CATEGORY_RESOURCES[categoryId ?? this._DEFAULT_USER_AD_CATEGORY]
  }

  @inlineError
  async getAd({categoryId, draftEntity}) {
    const adId = draftEntity.getAdId()
    const userId = draftEntity.getUserId()
    const categoryResource = this._getCategoryResource(categoryId)

    const url = `${this._publisherUsersUrl}${userId}/ads/${categoryResource}/${adId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError,
        [this._NOT_FOUND_STATUS_CODE]: this._notFoundDraftError
      })
      .get(url)

    if (error) return Promise.reject(error)

    const entity = this._draftEntityFactory(
      createResponseDraft({...response.data, adId: response.data.adId ?? adId, userId: response.data.userId ?? userId})
    )

    return this._ongoingDraftResponseFactory({
      status: response.status,
      draftEntity: entity
    })
  }

  @inlineError
  async ongoingDraft({categoryId, userEntity}) {
    const userId = userEntity.getId()
    const params = {}
    const categoryResource = this._getCategoryResource(categoryId)

    const url = `${this._publisherUsersUrl}${userId}/drafts/${categoryResource}/ongoing`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError,
        [this._NOT_FOUND_STATUS_CODE]: this._notFoundDraftError
      })
      .post(url, params)

    if (error) return Promise.reject(error)

    const draftEntity = this._draftEntityFactory(createResponseDraft({...response.data}))

    return this._ongoingDraftResponseFactory({
      status: response.status,
      draftEntity
    })
  }

  @inlineError
  async discardDraft(draft) {
    const {adId, categoryId, userId} = draft
    const categoryResource = this._getCategoryResource(categoryId)

    const url = `${this._publisherUsersUrl}${userId}/drafts/${categoryResource}/${adId}`
    const params = {userId}

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError,
        [this._NOT_FOUND_STATUS_CODE]: this._notFoundDraftError
      })
      .delete(url, params)

    if (error) return Promise.reject(error)

    return this._successResponseDraftVOFactory()
  }

  @inlineError
  async editDraft({categoryId, requestDraftVO}) {
    const draft = requestDraftVO.toJSON()
    const {adId, userId} = draft
    const categoryResource = this._getCategoryResource(categoryId)

    const url = `${this._publisherUsersUrl}${userId}/drafts/${categoryResource}/${adId}`

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError,
        [this._NOT_FOUND_STATUS_CODE]: this._notFoundDraftError
      })
      .put(url, draft)

    if (error) return Promise.reject(error)

    return this._successResponseDraftVOFactory()
  }

  @inlineError
  async generateComment({draftEntity}) {
    const {id: draftId, userId} = draftEntity.toJSON()

    const url = `${this._apiUrl}/users/${userId}/ads/${draftId}/fields/generate`

    return this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError
      })
      .put(url)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)

        const {
          data: {colorId, description}
        } = response
        return this._draftEntityFactory({id: draftId, userId, colour: colorId, comments: description})
      })
  }

  @inlineError
  async publishDraft({categoryId, requestDraftVO}) {
    const draft = requestDraftVO.toJSON()
    const categoryResource = this._getCategoryResource(categoryId)
    const url = `${this._publisherUsersUrl}${draft.userId}/drafts/${categoryResource}/${draft.adId}/publish`

    return this._postPublish({url, body: draft})
  }

  @inlineError
  async publishAd({categoryId, requestDraftVO}) {
    const adId = requestDraftVO.getAdId()
    const userId = requestDraftVO.getUserId()
    const categoryResource = this._getCategoryResource(categoryId)

    const url = `${this._publisherUsersUrl}${userId}/ads/${categoryResource}/${adId}`

    return this._postPublish({url, body: requestDraftVO.toAdRequestBody({categoryId})})
  }

  _postPublish({url, body}) {
    return this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError
      })
      .post(url, body)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)

        const {
          data: {code, resultCode}
        } = response
        return this._resultPublishDraftVOFactory({
          resultCode: resultCode || code
        })
      })
  }

  @inlineError
  async updateAd({categoryId, requestDraftVO, userEntity}) {
    const adId = requestDraftVO.getAdId()
    const userId = userEntity.getId()
    const categoryResource = this._getCategoryResource(categoryId)

    const url = `${this._publisherUsersUrl}${userId}/ads/${categoryResource}/${adId}`

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError,
        [this._NOT_FOUND_STATUS_CODE]: this._notFoundDraftError
      })
      .put(url, requestDraftVO.toAdRequestBody({categoryId}))

    if (error) return Promise.reject(error)

    return this._successResponseDraftVOFactory()
  }

  @inlineError
  async uploadPhotoAd({adId, image, userEntity}) {
    const userId = userEntity.getId()
    const url = `${this._publisherUsersUrl}${userId}/ads/${adId}/images`

    const formData = new FormData()
    formData.append('file', image)

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError,
        [this._NOT_FOUND_STATUS_CODE]: this._notFoundDraftError
      })
      .post(url, formData)

    if (error) return Promise.reject(error)

    return this._uploadResponseDraftVOFactory({url: response.data.originalUrl})
  }

  @AsyncInlineError()
  async renewAd({adId, userEntity}) {
    const userId = userEntity.getId()
    const url = `${this._apiUrl}/users/${userId}/renewed-ads/${adId}`

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError
      })
      .post(url)

    if (error) return Promise.reject(error)

    return this._successResponseDraftVOFactory()
  }

  @inlineError
  async uploadPhotoDraft({adId, image, userEntity}) {
    const userId = userEntity.getId()
    const url = `${this._publisherUsersUrl}${userId}/drafts/${adId}/images`

    const formData = new FormData()
    formData.append('file', image)

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: this._invalidDraftError,
        [this._NOT_FOUND_STATUS_CODE]: this._notFoundDraftError
      })
      .post(url, formData)

    if (error) return Promise.reject(error)

    return this._uploadResponseDraftVOFactory({url: response.data.originalUrl})
  }

  @AsyncInlineError()
  async getFreeAdsAvailable({userEntity}) {
    const userId = userEntity.getId()
    const section = this._config.get('search').DEFAULT_CATEGORY1_ID

    const url = `${this._apiUrl}/users/${userId}/free-ads-available?section=${section}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._GENERIC_ERROR]: data => new GetFreeAdsAvailableAdPublisherError(data)
      })
      .get(url)

    if (error) return Promise.reject(error)

    return FreeAdsAvailableAdPublisherValueObject.createFromApiResponse(response.data)
  }

  @AsyncInlineError()
  async getAdIdByCategory({categoryId, userEntity}) {
    const userId = userEntity.getId()

    const createError = () =>
      GetAdIdByCategoryAdPublisherError.create({
        message: `[HTTPDraftRepository#getAdIdByCategory] Error getting the ad id for ${categoryId}`
      })

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({[this._GENERIC_ERROR]: createError})
      .get(`${this._apiUrl}/users/${userId}/ads/${categoryId}/id/`)

    if (error) return Promise.reject(error)

    const {adId} = response.data

    try {
      IdSharedValueObject.validate({id: adId})
    } catch {
      return Promise.reject(createError())
    }

    return UserAdEntity.create({adId, category: categoryId})
  }
}

function createResponseDraft(response) {
  return {
    id: response.adId,
    adTitle: response.title,
    body: response.bodyTypeIdWithDoors,
    carPlate: response.carPlate,
    colour: response.colourId,
    colourExtra: response.colourExtra,
    comments: response.comments,
    email: response.email,
    environmentalLabel: response.environmentalLabel,
    fuel: response.fuelId,
    hidePhone: response.hidePhone,
    images: response.images,
    km: response.mileage,
    make: response.makeId,
    model: response.modelId,
    phone: response.phone,
    plateRegistrationDate: response.plateRegistrationDate,
    price: response.price,
    province: response.provinceId,
    publishInMA: response.publishInVibbo,
    transmission: response.transmissionTypeId,
    userId: response.userId,
    version: response.versionId,
    year: response.year,
    zipCode: response.zipCode
  }
}
